import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  ArrowRight,
  ArrowLeft,
  Siren,
  Volume2,
  ScanEye,
  Moon,
  ShieldCheck,
  Server,
  FileText,
  MapPin,
  Zap,
  Plug,
  Save,
  Mic,
  Ruler,
  X,
} from 'lucide-react';
import SeriesNav from '../components/SeriesNav';
import './ActiveDeterrenceCamera.css';

const HERO_IMG = '/images/active-deterrence.png';

const ActiveDeterrenceCamera = () => {
  const features = [
    {
      Icon: Siren,
      red: true,
      title: '警告灯による抑止',
      desc: '赤青警告灯により、侵入や不審な動きへの注意喚起に役立ちます。',
    },
    {
      Icon: Volume2,
      title: '音声による確認・案内',
      desc: '内蔵マイク・スピーカーにより、現場の音声確認や呼びかけに対応します。',
    },
    {
      Icon: ScanEye,
      title: '人・車両検知',
      desc: '人や車両の検知に対応し、必要な確認や録画管理をサポートします。',
    },
    {
      Icon: Moon,
      title: '夜間の確認にも対応',
      desc: '赤外線や白色灯により、夜間や暗い場所での確認に対応します。',
    },
    {
      Icon: ShieldCheck,
      title: '屋外設置に対応',
      desc: 'IP66対応モデルがあり、屋外の駐車場・資材置場・施設外周などにも使用できます。',
    },
    {
      Icon: Server,
      title: '録画機器との連携',
      desc: 'NVRやネットワーク機器と組み合わせて、防犯カメラシステムとして構成できます。',
    },
  ];

  const locations = [
    '駐車場',
    '資材置場',
    '夜間の無人施設',
    '工場・倉庫まわり',
    '施設外周',
    '建物出入口',
    '屋外設備',
  ];

  const models = [
    {
      name: 'HS-IE40BBWシリーズ',
      type: 'ドーム型',
      image: '/images/hs-ie40bbw.png',
      diagram: '/images/hs-ie40bbw-diagram.png',
      modelList: ['HS-BC-IE40BBW-5M3', 'HS-BC-IE40BBW-8M6'],
      specs: [
        '5MP / 8MP',
        '電動バリフォーカル 5X 2.7〜13.5mm',
        '赤外線 最大35m / 白色 最大20m',
      ],
    },
    {
      name: 'HS-IE35BBWシリーズ',
      type: 'ドーム型',
      image: '/images/hs-ie35bbw.png',
      diagram: '/images/hs-ie35bbw-diagram.png',
      modelList: ['HS-BC-IE35BBW-5M3', 'HS-BC-IE35BBW-8M6'],
      specs: [
        '5MP / 8MP',
        '電動バリフォーカル 5X 2.7〜13.5mm',
        '赤外線 最大35m / 白色 最大20m',
      ],
    },
    {
      name: 'HS-IB682BWシリーズ',
      type: 'バレット型',
      image: '/images/hs-ib682bw.png',
      diagram: '/images/hs-ib682bw-diagram.png',
      modelList: ['HS-BC-IB682BW-5M3', 'HS-BC-IB682BW-8M6', 'HS-BC-IB682BW-8MF'],
      specs: [
        '5MP / 8MP',
        '固定レンズ 2.8mm',
        '赤外線 最大25m / 白色 最大15m',
      ],
    },
    {
      name: 'HS-IB788BWシリーズ',
      type: 'バレット型',
      image: '/images/hs-ib788bw.png',
      diagram: '/images/hs-ib788bw-diagram.png',
      modelList: ['HS-BC-IB788BW-5M3', 'HS-BC-IB788BW-8M6', 'HS-BC-IB788BW-8MF'],
      specs: [
        '5MP / 8MP',
        '電動バリフォーカル 5X 2.7〜13.5mm',
        '赤外線 最大40m / 白色 最大25m',
      ],
    },
  ];

  // Common feature badges — all 4 models share these
  const commonBadges = [
    { Icon: Zap, label: 'PoE対応' },
    { Icon: Plug, label: 'DC12V対応' },
    { Icon: ShieldCheck, label: 'IP66' },
    { Icon: Save, label: 'microSD対応' },
    { Icon: ScanEye, label: '人・車両検知' },
    { Icon: Mic, label: '音声対応' },
  ];

  const compareRows = [
    {
      series: 'HS-IE40BBWシリーズ',
      shape: 'ドーム型',
      lens: '電動バリフォーカル',
      res: '5MP / 8MP',
      range: '赤外線 最大35m / 白色 最大20m',
      use: '出入口・施設外周・屋外監視',
    },
    {
      series: 'HS-IE35BBWシリーズ',
      shape: 'ドーム型',
      lens: '電動バリフォーカル',
      res: '5MP / 8MP',
      range: '赤外線 最大35m / 白色 最大20m',
      use: '出入口・施設まわり',
    },
    {
      series: 'HS-IB682BWシリーズ',
      shape: 'バレット型',
      lens: '固定レンズ 2.8mm',
      res: '5MP / 8MP',
      range: '赤外線 最大25m / 白色 最大15m',
      use: '駐車場・資材置場・屋外設備',
    },
    {
      series: 'HS-IB788BWシリーズ',
      shape: 'バレット型',
      lens: '電動バリフォーカル',
      res: '5MP / 8MP',
      range: '赤外線 最大40m / 白色 最大25m',
      use: '広めの駐車場・施設外周・車両出入口まわり',
    },
  ];

  const notes = [
    '警告灯や音声機能の使用方法は、設置場所や周辺環境に合わせて調整が必要です。',
    '夜間の映像や照射距離は、周囲の明るさ・設置位置・対象物までの距離により異なります。',
    '屋外設置の場合は、配線経路・取付位置・防水処理の確認が必要です。',
    '機種や設定内容により、使用できる機能が異なる場合があります。',
    '録画日数や保存容量は、カメラ台数・解像度・NVR容量により異なります。',
  ];

  // Lightbox state for 外形寸法図
  const [openDiagram, setOpenDiagram] = useState(null);

  useEffect(() => {
    if (!openDiagram) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpenDiagram(null);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [openDiagram]);

  return (
    <div className="adc-page" data-testid="active-deterrence-page">
      {/* ===== Hero ===== */}
      <section className="adc-hero" data-testid="adc-hero">
        <div className="container">
          <SeriesNav currentId="active" />

          <div className="adc-hero-inner">
            <div className="adc-hero-body">
              <Link
                to="/products/security-camera"
                className="adc-back-link"
                data-testid="back-link-top"
              >
                <ArrowLeft size={14} strokeWidth={2} />
                防犯カメラ一覧へ戻る
              </Link>

              <span className="adc-hero-eyebrow">
                <span className="adc-hero-eyebrow-dot" aria-hidden="true" />
                ACTIVE DETERRENCE CAMERA
              </span>

              <h1 className="adc-hero-title">アクティブ防犯カメラシリーズ</h1>
              <span className="adc-hero-divider" aria-hidden="true" />

              <p className="adc-hero-lead">
                警告灯や音声機能により、不審者への抑止を重視した防犯カメラシリーズです。<br />
                夜間の無人施設、資材置場、駐車場、工場・倉庫まわり、施設外周などに向いています。
              </p>

              <ul className="adc-hero-tags" data-testid="hero-tags">
                <li className="is-red">赤青警告灯</li>
                <li>双方向音声</li>
                <li>人・車両検知</li>
                <li>PoE対応</li>
                <li>IP66</li>
                <li>夜間撮影</li>
              </ul>
            </div>

            <div className="adc-hero-photo" data-testid="hero-photo">
              {HERO_IMG ? (
                <img src={HERO_IMG} alt="アクティブ防犯カメラシリーズ 製品イメージ" />
              ) : (
                <div className="adc-hero-photo-placeholder">IMAGE</div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== 2. Features ===== */}
      <section className="adc-section" id="features" data-testid="section-features">
        <div className="container">
          <div className="adc-section-head">
            <span className="adc-eyebrow">FEATURES</span>
            <h2 className="adc-title">このシリーズの特長</h2>
          </div>

          <div className="adc-feature-grid">
            {features.map((f, i) => {
              const Icon = f.Icon;
              return (
                <div
                  key={i}
                  className={'adc-feature-card' + (f.red ? ' is-red' : '')}
                  data-testid={`feature-card-${i}`}
                >
                  <div className="adc-feature-head">
                    <span className="adc-feature-icon">
                      <Icon size={22} strokeWidth={1.6} />
                    </span>
                    <h3 className="adc-feature-title">{f.title}</h3>
                  </div>
                  <p className="adc-feature-desc">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== 3. Locations ===== */}
      <section className="adc-section adc-section-alt" id="locations" data-testid="section-locations">
        <div className="container">
          <div className="adc-section-head">
            <span className="adc-eyebrow">SUITABLE LOCATIONS</span>
            <h2 className="adc-title">適した設置場所</h2>
          </div>

          <div className="adc-location-list">
            {locations.map((loc, i) => (
              <span key={i} className="adc-location-item" data-testid={`location-item-${i}`}>
                <span className="adc-location-dot" aria-hidden="true" />
                <MapPin size={14} strokeWidth={1.8} />
                {loc}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 4. Model lineup ===== */}
      <section className="adc-section" id="models" data-testid="section-models">
        <div className="container">
          <div className="adc-section-head">
            <span className="adc-eyebrow">MODEL LINEUP</span>
            <h2 className="adc-title">モデルラインアップ</h2>
          </div>

          <div className="adc-model-grid">
            {models.map((m, i) => (
              <div key={i} className="adc-model-card" data-testid={`model-card-${i}`}>
                <div className="adc-model-media">
                  <div className="adc-model-image">
                    {m.image ? (
                      <img src={m.image} alt={m.name} />
                    ) : (
                      <div className="adc-model-image-placeholder">IMAGE</div>
                    )}
                  </div>

                  <div className="adc-model-modelnames" data-testid={`model-list-${i}`}>
                    <span className="adc-block-label">対応モデル</span>
                    <div className="adc-model-models">
                      {m.modelList.map((mm, j) => (
                        <span key={j} className="adc-model-tag">
                          {mm}
                        </span>
                      ))}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="adc-model-thumb"
                    onClick={() => setOpenDiagram(m)}
                    data-testid={`diagram-open-${i}`}
                    disabled={!m.diagram}
                    aria-label={`${m.name} 外形寸法図を見る`}
                  >
                    <span className="adc-model-thumb-label">
                      <Ruler size={12} strokeWidth={2} />
                      外形寸法図
                    </span>
                    <span className="adc-model-thumb-box">
                      {m.diagram ? (
                        <img
                          src={m.diagram}
                          alt={`${m.name} 外形寸法図`}
                          loading="lazy"
                        />
                      ) : (
                        <span className="adc-model-thumb-empty">準備中</span>
                      )}
                      <span className="adc-model-thumb-cta">
                        クリックで拡大
                        <ArrowRight size={11} strokeWidth={2.2} />
                      </span>
                    </span>
                  </button>
                </div>

                <div className="adc-model-body">
                  <div className="adc-model-head">
                    <h3 className="adc-model-name" data-testid={`model-name-${i}`}>
                      {m.name}
                    </h3>
                    <span className="adc-model-type">{m.type}</span>
                  </div>

                  <p className="adc-model-desc">{m.desc}</p>

                  <div>
                    <p className="adc-block-label">主な機能</p>
                    <div className="adc-badge-row" data-testid={`feature-badges-${i}`}>
                      {commonBadges.map(({ Icon, label }, k) => (
                        <span key={k} className="adc-badge">
                          <Icon size={13} strokeWidth={1.8} />
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="adc-block-label">主要仕様</p>
                    <ul className="adc-spec-list">
                      {m.specs.map((s, j) => (
                        <li key={j}>{s}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="adc-model-actions">
                    <span
                      className="adc-btn-primary"
                      aria-disabled="true"
                      data-testid={`pdf-btn-${i}`}
                      title="仕様書は準備中です"
                    >
                      <FileText size={16} strokeWidth={2} />
                      仕様書PDF
                    </span>
                    </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 5. Comparison table ===== */}
      <section className="adc-section adc-section-alt" id="compare" data-testid="section-compare">
        <div className="container">
          <div className="adc-section-head">
            <span className="adc-eyebrow">COMPARISON</span>
            <h2 className="adc-title">比較表</h2>
          </div>

          <div className="adc-compare-wrap">
            <table className="adc-compare-table" data-testid="compare-table">
              <thead>
                <tr>
                  <th>シリーズ</th>
                  <th>形状</th>
                  <th>レンズ</th>
                  <th>解像度</th>
                  <th>照射距離</th>
                  <th>向いている用途</th>
                </tr>
              </thead>
              <tbody>
                {compareRows.map((r, i) => (
                  <tr key={i} data-testid={`compare-row-${i}`}>
                    <td>{r.series}</td>
                    <td>{r.shape}</td>
                    <td>{r.lens}</td>
                    <td>{r.res}</td>
                    <td>{r.range}</td>
                    <td>{r.use}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ===== 6. Notes ===== */}
      <section className="adc-section" id="notes" data-testid="section-notes">
        <div className="container">
          <div className="adc-section-head">
            <span className="adc-eyebrow">NOTES</span>
            <h2 className="adc-title">ご確認事項</h2>
          </div>

          <div className="adc-notes-card">
            <ul className="adc-notes-list">
              {notes.map((n, i) => (
                <li key={i} data-testid={`note-${i}`}>{n}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ===== 7. CTA ===== */}
      <section className="adc-cta" data-testid="section-cta">
        <div className="container">
          <div className="adc-cta-card">
            <span className="adc-cta-eyebrow">CONTACT</span>
            <h2 className="adc-cta-title">
              アクティブ防犯カメラについてご相談ください
            </h2>
            <p className="adc-cta-body">
              設置場所や目的に合わせて、カメラ本体・録画機器・周辺機器の構成をご案内いたします。
            </p>
            <Link to="/contact" className="adc-cta-btn" data-testid="cta-btn">
              このシリーズについて相談する
              <ArrowRight size={18} strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== Bottom back link ===== */}
      <section className="adc-bottom-nav" data-testid="section-bottom-nav">
        <div className="container">
          <Link
            to="/products/security-camera"
            className="adc-bottom-back"
            data-testid="back-link-bottom"
          >
            <ArrowLeft size={16} strokeWidth={2} />
            防犯カメラ一覧へ戻る
          </Link>
        </div>
      </section>

      {/* ===== 外形寸法図 lightbox ===== */}
      {openDiagram && (
        <div
          className="adc-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`${openDiagram.name} 外形寸法図`}
          onClick={() => setOpenDiagram(null)}
          data-testid="diagram-lightbox"
        >
          <div
            className="adc-lightbox-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="adc-lightbox-head">
              <div className="adc-lightbox-title">
                <span className="adc-block-label">外形寸法図</span>
                <h4>{openDiagram.name}</h4>
              </div>
              <button
                type="button"
                className="adc-lightbox-close"
                onClick={() => setOpenDiagram(null)}
                aria-label="閉じる"
                data-testid="diagram-close"
              >
                <X size={18} strokeWidth={2} />
              </button>
            </div>
            <div className="adc-lightbox-body">
              <img
                src={openDiagram.diagram}
                alt={`${openDiagram.name} 外形寸法図`}
                className="adc-lightbox-img"
              />
            </div>
            <p className="adc-lightbox-note">
              ※図中の寸法単位はmmです。設置環境により実際の取付位置は異なる場合があります。
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveDeterrenceCamera;
